import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import Notification from '@this/src/notification';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import type HotelOrderItemEanTotal from '@this/src/domain/arrangement/hotel_order_item_ean_total';
import type Arranger from '@this/src/domain/arranger/arranger';
import type { ArrangerArgs } from '@this/src/domain/arranger/arranger';
import { Fetcher } from '@this/src/util';

interface Props {
  hotelOrderItemEanTotal: HotelOrderItemEanTotal;
  arrangers: Arranger[];
  currentArranger: Arranger | null;
  suppliedItemId: number | null;
}

interface ApproveResponse {
  hotel_order_item_ean_total_approval: {
    reservation_date: string;
    approved_at: string;
    approved_by: ArrangerArgs;
    supplied_item_id: number | null;
  };
}

const TotalTableRow: React.FC<Props> = observer(
  ({ hotelOrderItemEanTotal, arrangers, currentArranger, suppliedItemId }) => {
    const [loading, setLoading] = useState(false);

    const handleApprove = useCallback(() => {
      setLoading(true);
      const params = {
        reservation_date: hotelOrderItemEanTotal.reservationDate.format('YYYY-MM-DD'),
        supplied_item_id: suppliedItemId
      };
      Fetcher.post<ApproveResponse>('/arrangement/auto_reservation_inventory/ean_hotel/total/confirm.json', params)
        .then(res => {
          const { reservation_date, approved_at, approved_by, supplied_item_id } =
            res.hotel_order_item_ean_total_approval;
          hotelOrderItemEanTotal.setApprovedAt(approved_at);
          hotelOrderItemEanTotal.setApprovedBy(approved_by?.id);
          hotelOrderItemEanTotal.setSuppliedItemId(supplied_item_id ?? undefined);
          Notification.success(`${reservation_date}の承認が完了しました`);
        })
        .catch(() => {
          Notification.error('承認に失敗しました');
        })
        .finally(() => setLoading(false));
    }, [hotelOrderItemEanTotal, suppliedItemId]);

    const handleCancel = useCallback(() => {
      const params = { reservation_date: hotelOrderItemEanTotal.reservationDate.format('YYYY-MM-DD') };
      Fetcher.delete('/arrangement/auto_reservation_inventory/ean_hotel/total/cancel', params)
        .then(() => {
          hotelOrderItemEanTotal.setApprovedAt(undefined);
          hotelOrderItemEanTotal.setApprovedBy(undefined);
          hotelOrderItemEanTotal.setSuppliedItemId(undefined);
          Notification.success('承認を取り消しました');
        })
        .catch(() => {
          Notification.error('承認の取り消しに失敗しました');
        });
    }, [hotelOrderItemEanTotal]);

    const reservationDate = hotelOrderItemEanTotal.reservationDate.format('YYYY-MM-DD');

    const hotelElementCsvParams = new URLSearchParams([['reservation_date', reservationDate]]);
    const hotelElementCsvUrl = `/arrangement/auto_reservation_inventory/ean_hotel/total/hotel_element_csv?${hotelElementCsvParams}`;

    const eanCsvParams = new URLSearchParams([['reservation_date', reservationDate]]);
    const eanCsvUrl = `/arrangement/auto_reservation_inventory/ean_hotel/total/ean_csv?${eanCsvParams}`;

    return (
      <TableRow>
        <TableCell>{reservationDate}</TableCell>
        <TableCell align="right">{hotelOrderItemEanTotal.hotelElementTotal?.toLocaleString() || 0}</TableCell>
        <TableCell align="right">
          {hotelOrderItemEanTotal.orderItemOriginalPriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">{hotelOrderItemEanTotal.eanRecordTotal?.toLocaleString() || 0}</TableCell>
        <TableCell align="right">
          {hotelOrderItemEanTotal.orderItemPriceDetailsPriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">
          {hotelOrderItemEanTotal.orderItemOriginalPricePriceTotal?.toLocaleString() || 0}
        </TableCell>
        <TableCell align="right">{hotelOrderItemEanTotal.eanRecordPriceTotal?.toLocaleString() || 0}</TableCell>
        <TableCell>{hotelOrderItemEanTotal.exactMatch ? '○' : '×'}</TableCell>
        <TableCell>
          <Button href={hotelElementCsvUrl} isExternal>
            Hotel Element CSV
          </Button>
        </TableCell>
        <TableCell>
          <Button href={eanCsvUrl} isExternal>
            EAN CSV
          </Button>
        </TableCell>
        <TableCell>{hotelOrderItemEanTotal.approvedAt?.format('YYYY-MM-DD') || '─'}</TableCell>
        <TableCell>
          {hotelOrderItemEanTotal.approvedBy ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', minHeight: '35.5px' }}>
                  {arrangers.find(arranger => arranger.id === hotelOrderItemEanTotal.approvedBy)?.name}
                </div>
                {currentArranger?.id === hotelOrderItemEanTotal.approvedBy && (
                  <Button
                    onClick={handleCancel}
                    style={{ padding: 0, marginLeft: 20, minWidth: 20, height: 20, borderRadius: 10 }}
                  >
                    ×
                  </Button>
                )}
              </div>
              {hotelOrderItemEanTotal.suppliedItemId &&
                hotelOrderItemEanTotal.suppliedItemId !== suppliedItemId && (
                  <div style={{ fontSize: 11, color: '#a72c09' }}>承認時と仕入商品が異なります</div>
                )}
            </div>
          ) : (
            <Button onClick={handleApprove} loading={loading}>
              確認
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }
);

export default TotalTableRow;
